'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { MessageSquare, Phone, Star, Shield, X, Send } from 'lucide-react';
import { toast } from 'sonner';

type MessageForm = { subject: string; message: string; };

export default function HostCard() {
  const [showModal, setShowModal] = useState(false);
  const [sending, setSending] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm<MessageForm>();

  // BACKEND INTEGRATION: POST /api/messages/host
  const onSend = async (_data: MessageForm) => {
    setSending(true);
    await new Promise((r) => setTimeout(r, 1200));
    setSending(false);
    setShowModal(false);
    reset();
    toast.success('Message sent to Maria! She typically replies within 2 hours.');
  };

  return (
    <>
      <div className="card-base p-6">
        <h2 className="text-xl font-700 text-foreground mb-5">Meet Your Host</h2>
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-800 text-xl shrink-0">
            M
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-700 text-base text-foreground">Maria Santos</h3>
              <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                <Shield size={11} className="text-secondary" /> Verified Host
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className="flex items-center gap-1 text-xs text-muted-foreground"><Star size={11} className="text-accent" /> 4.97 rating</span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">3 years hosting</span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">200+ guests hosted</span>
            </div>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Hi! I&apos;m Maria, born and raised on the Costa Brava. I love sharing this beautiful region with guests from around the world. I&apos;m always available to help with local tips, restaurant recommendations, or anything you need during your stay.
            </p>
            <div className="flex flex-wrap gap-3 mt-4">
              <button
                onClick={() => setShowModal(true)}
                className="btn-primary text-sm py-2 px-4"
              >
                <MessageSquare size={15} />
                Message Maria
              </button>
              <a href="tel:+34600123456" className="btn-outline text-sm py-2 px-4">
                <Phone size={15} />
                +34 600 123 456
              </a>
            </div>
          </div>
        </div>
        <div className="mt-5 pt-4 border-t border-border grid grid-cols-3 gap-4">
          {[
            { label: 'Response Rate', value: '100%' },
            { label: 'Response Time', value: '< 2 hrs' },
            { label: 'Languages', value: 'ES, EN, FR' },
          ].map((stat) => (
            <div key={`host-stat-${stat.label}`} className="text-center">
              <p className="text-base font-700 text-foreground tabular-nums">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Message Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-card w-full max-w-md rounded-2xl shadow-2xl p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-700 text-base text-foreground">Message Maria</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit(onSend)} className="space-y-4">
              <div>
                <label className="block text-xs font-700 uppercase tracking-wider text-muted-foreground mb-1.5">Subject</label>
                <input
                  {...register('subject', { required: 'Subject required' })}
                  placeholder="Question about the apartment"
                  className="input-base"
                />
                {errors.subject && <p className="mt-1 text-xs text-red-500">{errors.subject.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-700 uppercase tracking-wider text-muted-foreground mb-1.5">Message</label>
                <textarea
                  {...register('message', { required: 'Message required', minLength: { value: 10, message: 'At least 10 characters' } })}
                  rows={4}
                  placeholder="Hi Maria, I was wondering..."
                  className="input-base resize-none"
                />
                {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message.message}</p>}
              </div>
              <button type="submit" disabled={sending} className="w-full btn-primary justify-center disabled:opacity-60" style={{ minHeight: '44px' }}>
                {sending ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  <><Send size={15} /> Send Message</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}